<div class="page-head">
  <div>
    <p class="eyebrow">งานอนุมัติ</p>
    <h1>อนุมัติใบขอเบิก</h1>
    <p>ตรวจสอบรายละเอียดและพิจารณารายการขอเบิกที่รอดำเนินการ</p>
  </div>
</div>

<section class="rounded-xl border border-slate-200 bg-white shadow-sm">
  <p-table
    #expenseApprovalTable
    [value]="rows()"
    dataKey="id"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [globalFilterFields]="[
      'code',
      'bookingCode',
      'title',
      'expenseNameSnapshot',
      'bankName',
      'accountNumber',
      'requester',
      'department',
      'customerName',
      'tripName',
      'status',
    ]"
    [rowHover]="true"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="แสดง {first} ถึง {last} จาก {totalRecords} รายการ"
    [tableStyle]="{ 'min-width': '1060px' }"
  >
    <ng-template #caption>
      <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <label class="relative block w-full max-w-md">
          <lucide-icon
            name="Search"
            [size]="17"
            class="pointer-events-none absolute top-1/2 left-2 z-10 -translate-y-1/2 text-slate-400"
          />
          <input
            pInputText
            #query
            class="h-11 w-full pr-3 text-sm placeholder:text-slate-400"
            style="padding-left: 2.75rem; border-radius: 0.5rem"
            placeholder="ค้นหาใบขอเบิก Booking รายการ หรือผู้ขอ"
            (input)="expenseApprovalTable.filterGlobal(query.value, 'contains')"
          />
        </label>
        <div class="flex items-center justify-end gap-3">
          <p class="m-0 text-xs text-slate-500">
            รออนุมัติทั้งหมด
            <span class="font-semibold text-slate-700">{{ rows().length }}</span>
            รายการ
          </p>
          <app-refresh-button
            label="รีเฟรชรายการอนุมัติใบขอเบิก"
            [loading]="isLoadingExpenses()"
            (refresh)="loadExpenses()"
          />
        </div>
      </div>
    </ng-template>
    <ng-template #header>
      <tr>
        <th class="w-14 text-center text-xs whitespace-nowrap">ลำดับ</th>
        <th pSortableColumn="code" class="min-w-56 text-xs whitespace-nowrap">
          ใบขอเบิก <p-sortIcon field="code" />
        </th>
        <th pSortableColumn="bookingCode" class="min-w-72 text-xs whitespace-nowrap">
          ทัวร์ / Booking และรายละเอียด <p-sortIcon field="bookingCode" />
        </th>
        <th pSortableColumn="requester" class="min-w-52 text-xs whitespace-nowrap">
          ผู้ขอเบิก <p-sortIcon field="requester" />
        </th>
        <th pSortableColumn="amount" class="w-44 text-right text-xs whitespace-nowrap">
          จำนวนเงิน <p-sortIcon field="amount" />
        </th>
        <th pSortableColumn="status" class="w-44 text-xs whitespace-nowrap">
          สถานะ <p-sortIcon field="status" />
        </th>
        <th class="w-28 text-right text-xs whitespace-nowrap">พิจารณา</th>
      </tr>
    </ng-template>
    <ng-template #body let-row let-rowIndex="rowIndex">
      <tr class="transition even:bg-slate-50/40 hover:bg-sky-50/60">
        <td class="py-3.5 text-center text-[13px] text-slate-400 tabular-nums align-top">
          {{ rowIndex + 1 }}
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold whitespace-nowrap text-sky-700">{{ row.code }}</p>
          <p class="mt-1 mb-0 text-xs leading-5 text-slate-500">
            {{ row.expenseNameSnapshot || 'ไม่ระบุประเภทค่าใช้จ่าย' }}
          </p>
          <p class="mt-1 mb-0 text-xs whitespace-nowrap text-slate-500">
            ส่งเมื่อ {{ row.submittedAt | date: 'dd MMM yyyy' }}
          </p>
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-semibold whitespace-nowrap text-slate-800">
            {{ row.tourDetails?.tourNo || row.bookingCode || '—' }}
          </p>
          <p class="mt-1 mb-0 max-w-72 text-xs leading-5 text-slate-500">
            {{ row.title || 'ไม่ระบุรายละเอียด' }}
          </p>
          @if (row.targetType !== 'GENERAL' && row.paymentAccountSource) {
            <p class="mt-1 mb-0 max-w-72 text-xs leading-5 text-sky-700">
              บัญชีรับเงิน:
              @if (row.paymentAccountSource === 'OTHER_BANK_ACCOUNT') {
                {{ row.bankName || 'ไม่ระบุธนาคาร' }} · {{ row.accountNumber || 'ไม่ระบุเลขบัญชี' }}
              } @else {
                ตามไฟล์อ้างอิง
              }
            </p>
          }
          @if (row.targetType !== 'GENERAL') {
            <p class="mt-1 mb-0 text-xs text-slate-500">ลูกค้า: {{ row.customerName || '—' }}</p>
          }
        </td>
        <td class="py-3.5 text-[13px] align-top">
          <p class="m-0 text-sm font-medium leading-5 text-slate-800">
            {{ row.requester || '—' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">ฝ่าย: {{ row.department || '—' }}</p>
        </td>
        <td
          class="py-3.5 text-right text-sm font-semibold whitespace-nowrap text-slate-800 tabular-nums align-top"
        >
          {{ row.amount | number: '1.2-2' }} บาท
        </td>
        <td class="py-3.5 align-top">
          <app-status-badge [status]="row.status" />
        </td>
        <td class="py-3.5 align-top">
          <div class="flex justify-end gap-1.5">
            @if (row.attachmentUrl) {
              <button
                type="button"
                class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
                title="เปิดไฟล์ใบสั่งซื้อ"
                (click)="openAttachment(row.attachmentUrl)"
              >
                <lucide-icon name="FileText" [size]="15" />
              </button>
            }
            <button
              type="button"
              class="tw-button inline-flex size-8 items-center justify-center rounded-lg border border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50"
              title="ดูรายละเอียด"
              (click)="openExpenseDetail(row)"
            >
              <lucide-icon name="Eye" [size]="15" />
            </button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template #emptymessage>
      <tr>
        <td colspan="7">
          <div class="empty">
            <b><lucide-icon name="ClipboardCheck" [size]="22" /></b>
            <h3>ไม่มีใบขอเบิกรออนุมัติ</h3>
            <p>รายการใหม่จะแสดงที่นี่</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</section>
@if (selectedExpense(); as expense) {
  <div
    class="fixed inset-0 z-[200] grid place-items-center overflow-y-auto bg-slate-950/45 p-4 backdrop-blur-[2px]"
    (click)="closeExpenseDetail()"
  >
    <section
      class="my-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      role="dialog"
      aria-modal="true"
      (click)="$event.stopPropagation()"
    >
      <div class="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-6">
        <div>
          <p class="mb-1 text-xs font-semibold tracking-wider text-sky-600 uppercase">
            {{ expense.code }}
          </p>
          <h2 class="m-0 text-xl font-bold text-slate-800">รายละเอียดใบขอเบิก</h2>
          <div class="mt-2"><app-status-badge [status]="expense.status" /></div>
        </div>
        <button
          type="button"
          class="tw-button inline-flex size-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          (click)="closeExpenseDetail()"
        >
          <lucide-icon name="X" [size]="19" />
        </button>
      </div>
      <div class="max-h-[68vh] overflow-y-auto p-5 sm:p-6">
        @if (expense.tourDetails; as tour) {
          <section>
            <h3 class="m-0 text-base font-bold text-slate-800">ข้อมูลทัวร์</h3>
            <dl
              class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              <div>
                <dt class="text-xs font-semibold text-slate-500">เลขที่ทัวร์</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ tour.tourNo }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">รหัสทัวร์</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ tour.tourCode }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">ชื่อทัวร์</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ tour.tourName }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">ประเทศ</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ tour.country || '—' }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">วันเดินทาง</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ tour.travelDate | date: 'dd MMM yyyy' }}
                </dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">วันเดินทางกลับ</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ tour.returnDate ? (tour.returnDate | date: 'dd MMM yyyy') : '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">จำนวนคนที่กำหนด</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ tour.requiredPax === null ? '—' : tour.requiredPax + ' คน' }}
                </dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">ยอดรวมทัวร์</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ (tour.tourTotalAmount | number: '1.2-2') + ' บาท' }}
                </dd>
              </div>
            </dl>
          </section>
        } @else if (expense.bookingCode) {
          <section>
            <h3 class="m-0 text-base font-bold text-slate-800">ข้อมูล Booking</h3>
            <dl
              class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              <div>
                <dt class="text-xs font-semibold text-slate-500">เลขที่ Booking</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.bookingCode }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">ลูกค้า</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.customerName }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">โปรแกรมทัวร์</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.tripName }}</dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">วันเดินทาง</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ expense.travelDate | date: 'dd MMM yyyy' }}
                </dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">จำนวนผู้เดินทาง</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ expense.travelerCount | number }} คน
                </dd>
              </div>
              <div>
                <dt class="text-xs font-semibold text-slate-500">ยอด Booking</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  {{ expense.bookingTotal | number: '1.2-2' }} บาท
                </dd>
              </div>
            </dl>
          </section>
        }
        <section [class.mt-6]="!!expense.tourDetails || !!expense.bookingCode">
          <h3 class="m-0 text-base font-bold text-slate-800">ข้อมูลรายการเบิก</h3>
          <dl
            class="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            <div>
              <dt class="text-xs font-semibold text-slate-500">เลขที่ใบขอเบิก</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.code }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ผู้ขอเบิก</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.requester }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ฝ่าย</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.department }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">ประเภทค่าใช้จ่าย</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">
                {{ expense.expenseNameSnapshot }}
              </dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">รายการ</dt>
              <dd class="mt-1 mb-0 font-semibold text-slate-800">{{ expense.title }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-slate-500">จำนวนเงิน</dt>
              <dd class="mt-1 mb-0 font-semibold text-emerald-700">
                {{ expense.amount | number: '1.2-2' }} บาท
              </dd>
            </div>
            @if (expense.paymentAccountSource) {
              <div class="sm:col-span-2 lg:col-span-3">
                <dt class="text-xs font-semibold text-slate-500">ข้อมูลบัญชีรับเงิน</dt>
                <dd class="mt-1 mb-0 font-semibold text-slate-800">
                  @if (expense.paymentAccountSource === 'OTHER_BANK_ACCOUNT') {
                    {{ expense.bankName || 'ไม่ระบุธนาคาร' }} ·
                    {{ expense.accountNumber || 'ไม่ระบุเลขที่บัญชี' }}
                  } @else {
                    ตามไฟล์อ้างอิง
                  }
                </dd>
              </div>
            }
            <div class="sm:col-span-2 lg:col-span-3">
              <dt class="text-xs font-semibold text-slate-500">ไฟล์ใบสั่งซื้อ</dt>
              <dd class="mt-1 mb-0 font-semibold text-sky-700">
                @if (expense.attachmentUrl) {
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 text-sky-700 decoration-sky-300 hover:text-sky-900"
                    title="ดูเอกสารแนบในแท็บใหม่"
                    (click)="openAttachment(expense.attachmentUrl)"
                  >
                    ดูเอกสารแนบ
                    <lucide-icon name="ExternalLink" [size]="15" />
                  </button>
                } @else {
                  <span class="text-slate-400">-</span>
                }
              </dd>
            </div>
          </dl>
        </section>
      </div>
      <div
        class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
      >
        <button
          type="button"
          class="tw-button h-11 rounded-xl border border-slate-300 bg-white px-5 text-[15px] font-semibold text-slate-700 hover:bg-slate-100"
          (click)="closeExpenseDetail()"
        >
          ปิด
        </button>
        @if (expense.status === 'SUPERVISOR_REVIEW') {
          <button
            type="button"
            class="tw-button inline-flex h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-5 text-[15px] font-semibold text-red-600 hover:bg-red-50"
            (click)="reject(expense)"
          >
            <lucide-icon name="X" [size]="17" /> ไม่อนุมัติ</button
          ><button
            type="button"
            class="tw-button inline-flex h-11 items-center gap-2 rounded-xl bg-emerald-600 px-5 text-[15px] font-semibold text-white hover:bg-emerald-700"
            (click)="approve(expense)"
          >
            <lucide-icon name="Check" [size]="17" /> อนุมัติ
          </button>
        }
      </div>
    </section>
  </div>
}
